import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Radio,
  Mic,
  ShieldCheck,
  RefreshCw,
  Activity,
  Users,
} from 'lucide-react';

import liveSocket from '../lib/liveSocket';
import rtcClient from '../lib/rtcClient';

const API_ROOT = '/api/comms';

function authHeaders(extra = {}) {
  const token = localStorage.getItem('access_token');

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function apiFetch(path, options = {}) {
  const response = await fetch(`${API_ROOT}${path}`, {
    ...options,
    headers: authHeaders(options.headers || {}),
  });

  let body = null;

  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    throw new Error(
      body?.detail ||
      body?.message ||
      `Request failed with HTTP ${response.status}`
    );
  }

  return body;
}

function formatEventTime(value) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function CommsPage() {
  const [health, setHealth] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [events, setEvents] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [transmitting, setTransmitting] = useState(false);
  const [pttBusy, setPttBusy] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [floorBusy, setFloorBusy] = useState(false);
  const [speakerMessage, setSpeakerMessage] = useState('');

  const selectedChannelRef = useRef('');
  const transmittingRef = useRef(false);
  const floorGrantedRef = useRef(false);
  const heartbeatRef = useRef(null);
  const joinedChannelRef = useRef('');
  const mountedRef = useRef(true);

  const selectedChannel =
    channels.find((channel) => channel.id === selectedChannelId) || null;

  const loadEvents = useCallback(async (channelId) => {
    if (!channelId) {
      setEvents([]);
      return;
    }

    try {
      const result = await apiFetch(
        `/events?channel_id=${encodeURIComponent(channelId)}`
      );

      setEvents(
        Array.isArray(result)
          ? result
          : result?.events || []
      );
    } catch (err) {
      console.error('Unable to load Comms events:', err);
      setEvents([]);
    }
  }, []);

  const loadComms = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);

    setError('');

    try {
      const [healthResult, channelResult] = await Promise.all([
        apiFetch('/health'),
        apiFetch('/channels'),
      ]);

      const loadedChannels = Array.isArray(channelResult)
        ? channelResult
        : channelResult?.channels || [];

      setHealth(healthResult);
      setChannels(loadedChannels);

      setSelectedChannelId((current) => {
        if (
          current &&
          loadedChannels.some((channel) => channel.id === current)
        ) {
          return current;
        }

        return loadedChannels[0]?.id || '';
      });
    } catch (err) {
      console.error('RapidX Comms load failed:', err);
      setError(err.message || 'Unable to load RapidX Comms.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadComms();
  }, [loadComms]);

  useEffect(() => {
    loadEvents(selectedChannelId);
  }, [selectedChannelId, loadEvents]);

  // ----------------------------------------------------------
  // RapidX production voice transport
  // ----------------------------------------------------------

  const stopPttHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const safeMute = useCallback((reason = 'safety mute') => {
    floorGrantedRef.current = false;
    transmittingRef.current = false;

    stopPttHeartbeat();

    try {
      rtcClient.setMicrophoneEnabled(false);
    } catch (err) {
      console.warn('RapidX microphone mute:', reason, err);
    }

    if (mountedRef.current) {
      setTransmitting(false);
    }
  }, [stopPttHeartbeat]);

  const startPttHeartbeat = useCallback(() => {
    stopPttHeartbeat();

    heartbeatRef.current = setInterval(() => {
      const channelId = selectedChannelRef.current;

      if (
        !floorGrantedRef.current ||
        !channelId ||
        !liveSocket.isConnected()
      ) {
        safeMute('floor/socket unavailable');
        return;
      }

      liveSocket.send({
        type: 'ptt_heartbeat',
        channel_id: channelId,
      });
    }, 5000);
  }, [safeMute, stopPttHeartbeat]);

  useEffect(() => {
    selectedChannelRef.current = selectedChannelId;
  }, [selectedChannelId]);

  useEffect(() => {
    transmittingRef.current = transmitting;
  }, [transmitting]);

  useEffect(() => {
    mountedRef.current = true;

    const token = localStorage.getItem('access_token');

    if (!token) {
      setError('RapidX authentication token unavailable.');
      return undefined;
    }

    const handleSocketOpen = () => {
      const channelId = selectedChannelRef.current;

      if (channelId) {
        liveSocket.send({
          type: 'comms_join',
          channel_id: channelId,
        });
      }
    };

    const handleSocketClose = () => {
      safeMute('WebSocket disconnected');

      if (mountedRef.current) {
        setVoiceReady(false);
        setFloorBusy(false);
      }
    };

    const handleCommsJoined = async (message) => {
      const channelId = selectedChannelRef.current;

      if (
        !channelId ||
        message.channel_id !== channelId
      ) {
        return;
      }

      joinedChannelRef.current = channelId;

      try {
        await rtcClient.initialize({
          channelId,
          liveSocket,
          apiRoot: API_ROOT,
        });

        if (!mountedRef.current) return;

        /*
         * The server returns the peers already present in the
         * channel as part of comms_joined. Consume that initial
         * peer list so this newly joined client establishes RTC
         * connections with the existing participants.
         */
        const initialPeers =
          Array.isArray(message.peers)
            ? message.peers
            : [];

        for (const peer of initialPeers) {
          if (!peer?.connection_id) continue;

          await rtcClient.handlePeerJoined(
            {
              ...peer,
              channel_id: channelId,
            },
            {
              initiator: true,
            }
          );
        }

        setVoiceReady(true);

        liveSocket.send({
          type: 'rtc_ready',
          channel_id: channelId,
        });
      } catch (err) {
        console.error(
          'RapidX voice initialization failed:',
          err
        );

        safeMute('RTC initialization failure');

        if (mountedRef.current) {
          setVoiceReady(false);
          setError(
            err?.message ||
              'Unable to initialise RapidX voice.'
          );
        }
      }
    };

    const handleRtcPeerJoined = async (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      try {
        await rtcClient.handlePeerJoined(
            message,
            {
              initiator: false,
            }
          );
      } catch (err) {
        console.error('RapidX peer join failed:', err);
      }
    };

    const handleRtcReady = async (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      try {
        /*
           * Peer discovery already occurs through comms_joined
           * and rtc_peer_joined. Do not start another RTC
           * negotiation from rtc_ready.
           */
          return;
      } catch (err) {
        console.error('RapidX peer ready failed:', err);
      }
    };

    const handleRtcOffer = async (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      try {
        await rtcClient.handleOffer(message);
      } catch (err) {
        console.error('RapidX RTC offer failed:', err);
      }
    };

    const handleRtcAnswer = async (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      try {
        await rtcClient.handleAnswer(message);
      } catch (err) {
        console.error('RapidX RTC answer failed:', err);
      }
    };

    const handleRtcIce = async (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      try {
        await rtcClient.handleIceCandidate(message);
      } catch (err) {
        console.error('RapidX RTC ICE failed:', err);
      }
    };

    const handlePttGranted = (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      floorGrantedRef.current = true;
      transmittingRef.current = true;

      rtcClient.setMicrophoneEnabled(true);

      setFloorBusy(false);
      setTransmitting(true);

      startPttHeartbeat();
    };

    const handlePttBusy = (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      safeMute('PTT floor busy');
      setFloorBusy(true);
    };

    const handleHeartbeatAck = (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      if (!message.renewed) {
        safeMute('PTT heartbeat rejected');
      }
    };

    const handlePttReleased = (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      safeMute('PTT release acknowledged');
    };

    const handleFloorChanged = (message) => {
      if (
        message.channel_id !== selectedChannelRef.current
      ) return;

      if (message.state === 'available') {
        setFloorBusy(false);

        if (floorGrantedRef.current) {
          safeMute('floor became available');
        }
      } else if (
        message.state === 'busy' &&
        !floorGrantedRef.current
      ) {
        setFloorBusy(true);
      }
    };

    const unsubscribers = [
      liveSocket.on('socket_open', handleSocketOpen),
      liveSocket.on('socket_close', handleSocketClose),
      liveSocket.on('comms_joined', handleCommsJoined),
      liveSocket.on('rtc_peer_joined', handleRtcPeerJoined),
      liveSocket.on('rtc_ready', handleRtcReady),
      liveSocket.on('rtc_offer', handleRtcOffer),
      liveSocket.on('rtc_answer', handleRtcAnswer),
      liveSocket.on(
        'rtc_ice_candidate',
        handleRtcIce
      ),
      liveSocket.on('ptt_granted', handlePttGranted),
      liveSocket.on('ptt_busy', handlePttBusy),
      liveSocket.on(
        'ptt_heartbeat_ack',
        handleHeartbeatAck
      ),
      liveSocket.on('ptt_released', handlePttReleased),
      liveSocket.on(
        'comms_floor_changed',
        handleFloorChanged
      ),
    ];

    liveSocket.connect(token);

    if (
      liveSocket.isConnected() &&
      selectedChannelRef.current
    ) {
      liveSocket.send({
        type: 'comms_join',
        channel_id: selectedChannelRef.current,
      });
    }

    return () => {
      mountedRef.current = false;

      safeMute('Comms page unmounted');

      unsubscribers.forEach((unsubscribe) => {
        try {
          unsubscribe?.();
        } catch {
          // No-op.
        }
      });

      try {
        rtcClient.destroy();
      } catch (err) {
        console.warn('RapidX RTC cleanup:', err);
      }
    };
  }, [
    safeMute,
    startPttHeartbeat,
  ]);

  useEffect(() => {
    if (!selectedChannelId) {
      return undefined;
    }

    safeMute('channel changed');
    setVoiceReady(false);
    setFloorBusy(false);

    try {
      rtcClient.destroy();
    } catch {
      // No-op.
    }

    if (liveSocket.isConnected()) {
      liveSocket.send({
        type: 'comms_join',
        channel_id: selectedChannelId,
      });
    }

    return () => {
      if (
        joinedChannelRef.current === selectedChannelId
      ) {
        joinedChannelRef.current = '';
      }
    };
  }, [selectedChannelId, safeMute]);

  const recordPttEvent = async (eventType) => {
    if (!selectedChannelId || pttBusy) return false;

    setPttBusy(true);
    setError('');

    try {
      await apiFetch('/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel_id: selectedChannelId,
          event_type: eventType,
        }),
      });

      await loadEvents(selectedChannelId);
      return true;
    } catch (err) {
      console.error('PTT event failed:', err);
      setError(err.message || 'Unable to record transmission event.');
      return false;
    } finally {
      setPttBusy(false);
    }
  };

  const startTransmit = async () => {
    if (
      transmittingRef.current ||
      pttBusy ||
      floorBusy ||
      !voiceReady ||
      !selectedChannelId ||
      !liveSocket.isConnected()
    ) {
      return;
    }

    setError('');

    try {
      /*
       * Mobile-safe activation:
       * getUserMedia() executes from the user's PTT gesture.
       *
       * activateMicrophone() always leaves the local audio track
       * muted. Transmission still cannot begin until the server
       * grants the PTT floor.
       */
      await rtcClient.activateMicrophone();
    } catch (err) {
      const microphoneError = {
        name: err?.name || 'UnknownError',
        message:
          err?.message ||
          'Unable to access the microphone.',
        code: err?.code ?? null,
        secureContext: window.isSecureContext,
        documentVisibility: document.visibilityState,
        documentHasFocus: document.hasFocus(),
        userAgent: navigator.userAgent,
      };

      console.error(
        'RapidX microphone activation failed:',
        microphoneError,
        err
      );

      safeMute('microphone activation failure');

      setError(
        `${microphoneError.name}: ${microphoneError.message}`
      );

      return;
    }

    // Microphone remains muted until server floor grant.
    safeMute('waiting for PTT grant');

    liveSocket.send({
      type: 'ptt_request',
      channel_id: selectedChannelId,
    });

    recordPttEvent('ptt_pressed');
  };

  const stopTransmit = async () => {
    const channelId = selectedChannelRef.current;

    // Local mute happens before network release.
    safeMute('local PTT release');

    if (
      channelId &&
      liveSocket.isConnected()
    ) {
      liveSocket.send({
        type: 'ptt_release',
        channel_id: channelId,
      });
    }

    recordPttEvent('ptt_released');
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-blue-500" />
          <p className="text-zinc-400">Connecting to RapidX Comms…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-blue-500/10 border border-blue-500/20">
              <Radio className="w-7 h-7 text-blue-500" />
            </div>

            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-zinc-100">
                RapidX Comms
              </h1>

              <p className="text-sm text-zinc-400 mt-1">
                Secure operational communications
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${
              health?.status === 'healthy'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                : 'border-red-500/30 bg-red-500/10 text-red-400'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                health?.status === 'healthy'
                  ? 'bg-emerald-400'
                  : 'bg-red-400'
              }`}
            />

            {health?.status === 'healthy'
              ? 'Connected'
              : 'Unavailable'}
          </div>

          <button
            type="button"
            onClick={() => loadComms(true)}
            disabled={refreshing}
            className="p-2.5 rounded-xl border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 disabled:opacity-50"
            title="Refresh Comms"
          >
            <RefreshCw
              className={`w-5 h-5 ${
                refreshing ? 'animate-spin' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
          <div className="p-5 border-b border-zinc-800">
            <label
              htmlFor="comms-channel"
              className="block text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-2"
            >
              Channel
            </label>

            <select
              id="comms-channel"
              value={selectedChannelId}
              onChange={(event) => {
                  safeMute('channel selection changed');
                  setSelectedChannelId(event.target.value);
              }}
              className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-zinc-100 outline-none focus:border-blue-500"
            >
              {channels.length === 0 && (
                <option value="">
                  No channels available
                </option>
              )}

              {channels.map((channel) => (
                <option
                  key={channel.id}
                  value={channel.id}
                >
                  {channel.name}
                </option>
              ))}
            </select>
          </div>

          <div className="px-6 py-10 md:py-14 text-center">
            <div className="flex justify-center mb-4">
              <ShieldCheck className="w-5 h-5 text-emerald-400 mr-2" />
              <span className="text-sm text-emerald-400">
                Tenant-secured channel
              </span>
            </div>

            <h2 className="text-xl md:text-2xl font-semibold text-zinc-100">
              {selectedChannel?.name || 'No Channel'}
            </h2>

            <p className="text-sm text-zinc-500 mt-2 max-w-lg mx-auto">
              {selectedChannel?.description ||
                'Select an operational communications channel.'}
            </p>

            <div className="mt-10 flex justify-center">
              <button
                type="button"
                disabled={
                  !selectedChannelId ||
                  pttBusy ||
                  floorBusy ||
                  !voiceReady
                }
                onMouseDown={startTransmit}
                onMouseUp={stopTransmit}
                onMouseLeave={() => {
                  if (transmitting) stopTransmit();
                }}
                onTouchStart={(event) => {
                  event.preventDefault();
                  startTransmit();
                }}
                onTouchEnd={(event) => {
                  event.preventDefault();
                  stopTransmit();
                }}
                className={`
                  w-48 h-48 md:w-56 md:h-56
                  rounded-full border-8
                  flex flex-col items-center justify-center
                  select-none touch-none
                  transition-all duration-150
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${
                    transmitting
                      ? 'bg-red-600 border-red-400 scale-95 shadow-[0_0_60px_rgba(239,68,68,0.35)]'
                      : 'bg-blue-600 border-blue-400 hover:bg-blue-500 shadow-[0_0_50px_rgba(59,130,246,0.20)]'
                  }
                `}
              >
                <Mic className="w-12 h-12 text-white mb-3" />

                <span className="text-white text-lg font-bold tracking-wider">
                  {transmitting
                    ? 'TRANSMITTING'
                    : 'HOLD TO TALK'}
                </span>
              </button>
            </div>

            <div className="mt-3 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const playing = await rtcClient.resumeRemoteAudio();
                    setSpeakerMessage(
                      playing
                        ? 'Speaker audio enabled.'
                        : 'No incoming audio stream yet. Tap again while someone is speaking.'
                    );
                  } catch (error) {
                    console.warn('RapidX speaker playback retry failed:', error);
                    setSpeakerMessage('Could not start audio. Tap to retry.');
                  }
                }}
                className="rounded-lg border border-blue-400/60 bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-100"
              >
                Enable speaker
              </button>
              {speakerMessage && (
                <p className="text-center text-xs text-zinc-400">{speakerMessage}</p>
              )}
            </div>

            <div className="mt-6">
              <div
                className={`inline-flex items-center gap-2 text-sm ${
                  transmitting
                    ? 'text-red-400'
                    : 'text-zinc-400'
                }`}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    transmitting
                      ? 'bg-red-400 animate-pulse'
                      : 'bg-emerald-400'
                  }`}
                />

                {transmitting
                  ? 'Transmission active'
                  : 'Ready'}
              </div>

              <p className="text-xs text-zinc-600 mt-3">
                {floorBusy
                  ? 'Channel busy — another user has the floor.'
                  : voiceReady
                    ? 'Secure live voice ready.'
                    : 'Preparing secure live voice…'}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
          <div className="p-5 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />

              <h3 className="font-semibold text-zinc-100">
                Recent Activity
              </h3>
            </div>
          </div>

          <div className="p-4">
            {events.length === 0 ? (
              <div className="py-10 text-center">
                <Users className="w-8 h-8 mx-auto text-zinc-700 mb-3" />
                <p className="text-sm text-zinc-500">
                  No transmissions yet
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {events.slice(0, 20).map((event, index) => (
                  <div
                    key={event.id || `${event.created_at}-${index}`}
                    className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-zinc-200">
                        {event.event_type === 'ptt_pressed'
                          ? 'PTT started'
                          : event.event_type === 'ptt_released'
                            ? 'PTT released'
                            : event.event_type || 'Comms event'}
                      </span>

                      <span className="text-xs text-zinc-500">
                        {formatEventTime(
                          event.created_at ||
                          event.timestamp
                        )}
                      </span>
                    </div>

                    {(event.user_name ||
                      event.user_email ||
                      event.user_id) && (
                      <p className="text-xs text-zinc-500 mt-1">
                        {event.user_name ||
                          event.user_email ||
                          event.user_id}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
